import * as Api from '@ViewModels';
import * as React from 'react';
import { useStreamingResponseMutation } from '../../hooks/useStreamingResponseMutation';
import { useErrorMessages, useUserSession } from '../../models/hooks/appStateHooks';
import { useEventLogging } from '../../models/Logging';
import { useAIAssistantCreateConversationMutation } from '../../queries/AI/useAIAssistantCreateConversationMutation';
import { getConversationChatMessageQueryKey } from '../../queries/AIConversation/useAIConversationChatMessageQuery';
import { reactQueryClient } from '../../web/ReactQueryProvider';
import { POST_CALL_SYSTEM_MESSAGE } from '../constants/systemMessages';
import { useLeadAIContext, useLeadAIContextData } from '../queries/LeadAIContext';
import { LeadServedSource } from '../viewModels/queue';

export const AIDA_ASSISTANT_NAME = 'SalesPostCallCoachingAgent';

export const useAISummary = (
	realtimeTranscriptionUtterances: string[],
	contextData?: {
		phoneCallId?: string;
		companyId?: string;
		leadServedId?: string;
		source?: LeadServedSource;
	}
) => {
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('useAISummary');
	const userSession = useUserSession();
	const [aiResponse, setAiResponse] = React.useState('');
	const [conversationId, setConversationId] = React.useState<string>(null);
	const [hasFetchedFeedback, setHasFetchedFeedback] = React.useState<boolean>(false);

	// Fetch HubSpot data and get cached data (properly scoped to current lead)
	const leadAIContextMutation = useLeadAIContext();
	const cachedHubspotData = useLeadAIContextData(contextData?.companyId);

	const aiAssistantCreateConversationMutation = useAIAssistantCreateConversationMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError('aiAssistantCreateConversationMutation', error);
		},
		onSuccess: conversation => {
			setConversationId(conversation.id);

			const formattedUtterances = realtimeTranscriptionUtterances.join('\n\n');
			const chatMessages: Api.AIChatMessage[] = [];

			// Add system message with post-call instructions
			chatMessages.push({
				role: Api.AIChatRole.System,
				text: POST_CALL_SYSTEM_MESSAGE,
				id: null,
			});

			// Include HubSpot data as a separate system message if available
			const hubspotData = cachedHubspotData?.data || leadAIContextMutation.data?.data;

			if (hubspotData) {
				const cleanedHubSpotData = hubspotData.replace(/\\n/g, '\n').replace(/\\r/g, '\r');
				chatMessages.push({
					role: Api.AIChatRole.System,
					text: `# HubSpot Lead AI Context\n\nThe following is comprehensive lead and company data from HubSpot that provides context about this prospect:\n\n${cleanedHubSpotData}`,
					id: null,
				});
			}

			// Add the call transcript as the user message
			chatMessages.push({
				role: Api.AIChatRole.User,
				text: `Here is the call transcript to analyze:\n\n${formattedUtterances}`,
				id: null,
			});

			aiPromptMutation.mutate({
				assistantName: AIDA_ASSISTANT_NAME,
				convoId: conversation.id,
				chatRequest: {
					chatMessages,
				},
			});
		},
	});

	const aiPromptMutation = useStreamingResponseMutation({
		mutationFn: ({
			assistantName,
			convoId,
			chatRequest,
		}: {
			assistantName: string;
			convoId: string;
			chatRequest: Api.AIMessageRequest;
		}) =>
			new Promise<Response>((resolve, reject) => {
				userSession.webServiceHelper.callWebServiceRawResponse(
					Api.ImpersonationBroker.composeApiUrl({
						urlPath: `ai/assistant/${assistantName}/conversation/${convoId}/stream`,
					}),
					'POST',
					chatRequest,
					resolve,
					reject
				);
			}),
		onEvent: (event, _params) => {
			const data = JSON.parse(event.data);
			if (event.event !== 'Response') {
				return;
			}

			const messageText = data?.message?.text;

			if (messageText) {
				if (data.isDelta === false) {
					setAiResponse(data.message.text);
				} else {
					setAiResponse(prev => {
						if (prev.endsWith(':') || prev.length === 0) {
							return prev + messageText;
						} else if (/^[.,;:!?\s]/.test(messageText)) {
							// No space if messageText starts with punctuation OR whitespace
							return prev + messageText;
						} else {
							return prev + ' ' + messageText;
						}
					});
				}

				reactQueryClient.setQueryData(
					getConversationChatMessageQueryKey({
						id: _params.convoId,
					}),
					(messages: Api.AIChatMessage[]) => {
						if (messages?.length > 0 && messages[messages.length - 1].role === Api.AIChatRole.Assistant) {
							const lastAssistantMessage = messages.pop();
							let updatedText = lastAssistantMessage.text;
							if (updatedText.endsWith(':') || updatedText.length === 0) {
								updatedText += messageText;
							} else if (/^[.,;:!?\s]/.test(messageText)) {
								// No space if messageText starts with punctuation OR whitespace
								updatedText += messageText;
							} else {
								updatedText += ' ' + messageText;
							}

							return [...messages, { ...lastAssistantMessage, text: updatedText }];
						}
						return [...(messages || []), { id: data.Value?.Id, text: messageText, role: Api.AIChatRole.Assistant }];
					}
				);
			}
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError('aiPrompt', error as Api.IOperationResultNoValue);
		},
		onSuccess: () => {
			setHasFetchedFeedback(true);
		},
	});

	const handleGetPostCallFeedback = React.useCallback(() => {
		if (aiAssistantCreateConversationMutation.isPending || aiPromptMutation.isStreaming || hasFetchedFeedback) return;

		setAiResponse('');

		// Since HubSpot data should be prefetched, we can proceed directly with conversation creation
		// The data will be included if it's available in the cache
		aiAssistantCreateConversationMutation.mutate({
			assistantName: AIDA_ASSISTANT_NAME,
			phoneCallId: contextData?.phoneCallId,
			companyId: contextData?.companyId,
			leadServedId: contextData?.leadServedId,
			source: contextData?.source,
		});
	}, [aiAssistantCreateConversationMutation, aiPromptMutation.isStreaming, hasFetchedFeedback, contextData]);

	const resetPostCallFeedback = React.useCallback(() => {
		setAiResponse('');
		setConversationId(null);
		setHasFetchedFeedback(false);
	}, []);

	const sendFollowUpQuestion = React.useCallback(
		(question: string) => {
			if (!conversationId || aiPromptMutation.isStreaming) return;

			setAiResponse('');

			aiPromptMutation.mutate({
				assistantName: AIDA_ASSISTANT_NAME,
				convoId: conversationId,
				chatRequest: {
					chatMessages: [
						{
							role: Api.AIChatRole.User,
							text: question,
							id: null,
						},
					],
				},
			});
		},
		[conversationId, aiPromptMutation]
	);

	return {
		aiResponse,
		isLoading:
			aiAssistantCreateConversationMutation.isPending ||
			aiPromptMutation.isLoading ||
			aiPromptMutation.isStreaming ||
			leadAIContextMutation.isPending,
		hasFetchedFeedback,
		handleGetPostCallFeedback,
		resetPostCallFeedback,
		sendFollowUpQuestion,
		conversationId,
	};
};
